import type { NostrEvent, NostrMetadata } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Newspaper } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { type FeedOptions, timeAgo } from '@/lib/feed';

interface FeedPreviewProps {
  pubkeys: string[];
  profiles: Map<string, NostrMetadata>;
  options: FeedOptions;
}

/** Live preview of what the combined feed would contain right now. */
export function FeedPreview({ pubkeys, profiles, options }: FeedPreviewProps) {
  const { nostr } = useNostr();

  const kinds: number[] = [];
  if (options.notes) kinds.push(1);
  if (options.articles) kinds.push(30023);

  const { data: events, isLoading } = useQuery({
    queryKey: ['nostrss', 'preview', [...pubkeys].sort(), options],
    queryFn: async (c) => {
      const results = await nostr.query([{ kinds, authors: pubkeys, limit: 40 }], { signal: c.signal });
      return results
        .filter((event) => options.replies || event.kind !== 1 || !event.tags.some(([name]) => name === 'e'))
        .sort((a, b) => b.created_at - a.created_at)
        .slice(0, 10);
    },
    enabled: pubkeys.length > 0 && kinds.length > 0,
    staleTime: 60 * 1000,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Preview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {isLoading ? (
          <PreviewSkeleton />
        ) : !events || events.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Nothing found yet. New posts will appear here once relays are reachable.
          </p>
        ) : (
          events.map((event) => (
            <PreviewItem key={event.id} event={event} metadata={profiles.get(event.pubkey)} />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function PreviewItem({ event, metadata }: { event: NostrEvent; metadata?: NostrMetadata }) {
  const name = metadata?.display_name || metadata?.name || `npub${event.pubkey.slice(0, 8)}`;
  const picture = metadata?.picture;

  const isArticle = event.kind === 30023;
  const articleTitle = isArticle ? event.tags.find(([tag]) => tag === 'title')?.[1] : undefined;
  const snippet = stripMedia(event.content);

  const href = isArticle
    ? `https://njump.me/${nip19.naddrEncode({
        kind: event.kind,
        pubkey: event.pubkey,
        identifier: event.tags.find(([tag]) => tag === 'd')?.[1] ?? '',
      })}`
    : `https://njump.me/${nip19.neventEncode({ id: event.id, author: event.pubkey })}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-muted group"
    >
      <Avatar className="size-8 border shrink-0">
        <AvatarImage src={picture} alt={name} />
        <AvatarFallback className="bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300 text-xs">
          {name.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium truncate">{name}</span>
          {isArticle && (
            <Badge variant="secondary" className="gap-1 shrink-0">
              <Newspaper className="size-3" />
              Article
            </Badge>
          )}
          <span className="text-xs text-muted-foreground shrink-0 ml-auto">{timeAgo(event.created_at)}</span>
        </div>
        <p className="text-sm text-muted-foreground line-clamp-2 break-words">
          {isArticle ? (articleTitle ?? snippet) : snippet}
        </p>
      </div>
      <ExternalLink className="size-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
    </a>
  );
}

function PreviewSkeleton() {
  return (
    <div className="space-y-3 py-1">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex gap-3 px-2">
          <Skeleton className="size-8 rounded-full shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-3.5 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

function stripMedia(content: string): string {
  return content.replace(/https?:\/\/\S+\.(png|jpe?g|gif|webp|avif)(\?\S*)?/gi, '').trim();
}
